import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  AvatarGroup,
  Avatar,
  Tabs,
  Tab,
  InputBase,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar
} from '@mui/material';
import {
  CalendarToday,
  PlayArrow,
  Search,
  Refresh,
  PersonAdd,
  VolumeUp,
  CloudDone,
  Close,
  Dashboard
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Meeting } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  fetchMeetingsFromApi,
  mapApiMeetingToMeeting,
  shareMeetingApi,
  formatNameFromEmail
} from '../services/meetingService';
import MeetingDetails from '../components/meetings/MeetingDetails';

const MeetingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, firebaseUser } = useAuth();

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState(0); // 0: Today, 1: Past, 2: My Meetings
  const [searchQuery, setSearchQuery] = useState('');

  // Selected meeting for preview dialog
  const [activeMeeting, setActiveMeeting] = useState<Meeting | null>(null);

  // Share Dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTargetMeeting, setShareTargetMeeting] = useState<Meeting | null>(null);
  const [shareEmail, setShareEmail] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const loadMeetings = useCallback(async (showToast = false) => {
    setIsLoading(true);
    try {
      const apiMeetings = await fetchMeetingsFromApi();
      if (apiMeetings && apiMeetings.length > 0) {
        const mapped = apiMeetings.map((m, idx) => mapApiMeetingToMeeting(m, idx));
        setMeetings(mapped);
        if (showToast) {
          setToastMessage(`Synced ${mapped.length} meeting(s) from Cloud Run`);
        }
      } else {
        setMeetings([]);
        if (showToast) {
          setToastMessage('0 meetings returned for this account.');
        }
      }
    } catch (err: any) {
      console.warn('MeetingsPage fetch error:', err.message);
      if (showToast) {
        setToastMessage(`Sync failed: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeetings(false);
  }, [loadMeetings, firebaseUser]);

  const isMeetingToday = (m: Meeting): boolean => {
    if (m.date.toLowerCase() === 'today') return true;
    if (m.meeting_time) {
      const d = new Date(m.meeting_time);
      if (!isNaN(d.getTime())) {
        const today = new Date();
        if (
          d.getFullYear() === today.getFullYear() &&
          d.getMonth() === today.getMonth() &&
          d.getDate() === today.getDate()
        ) {
          return true;
        }
      }
    }
    const todayFormatted = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    if (m.date === todayFormatted) return true;
    if (m.date.includes('Oct 16, 2026')) return true;
    return false;
  };

  const isMeetingPast = (m: Meeting): boolean => {
    return !isMeetingToday(m);
  };

  const isMyMeeting = (m: Meeting): boolean => {
    const userEmail = user?.email?.toLowerCase();
    if (!userEmail) return true;
    if (m.host_email?.toLowerCase() === userEmail) return true;
    return (m.participants || []).some((p) => p.email?.toLowerCase() === userEmail);
  };

  const filteredMeetings = meetings.filter((m) => {
    if (tab === 0 && !isMeetingToday(m)) return false;
    if (tab === 1 && !isMeetingPast(m)) return false;
    if (tab === 2 && !isMyMeeting(m)) return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = m.title.toLowerCase().includes(q);
      const matchDate = m.date.toLowerCase().includes(q);
      const matchHost = m.host_email?.toLowerCase().includes(q) || false;
      const matchPart = (m.participants || []).some(
        (p) => p.name.toLowerCase().includes(q) || (p.email && p.email.toLowerCase().includes(q))
      );
      if (!matchTitle && !matchDate && !matchHost && !matchPart) {
        return false;
      }
    }
    return true;
  });

  const handleShareSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!shareTargetMeeting) return;

    const email = shareEmail.trim();
    if (!email || !email.includes('@')) {
      setShareError('Please enter a valid email address.');
      return;
    }

    setIsSharing(true);
    setShareError(null);

    try {
      const meetingId = shareTargetMeeting.meeting_id || shareTargetMeeting.id;
      await shareMeetingApi(meetingId, email);

      const newName = formatNameFromEmail(email);
      const newParticipant = {
        id: `p-${Date.now()}-${email}`,
        name: newName,
        email,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newName)}&background=FF6577&color=fff`
      };

      setMeetings((prev) =>
        prev.map((m) => {
          if (m.id === shareTargetMeeting.id || m.meeting_id === shareTargetMeeting.meeting_id) {
            const currentParts = m.participants || [];
            if (!currentParts.some((p) => p.email === email)) {
              return { ...m, participants: [...currentParts, newParticipant] };
            }
          }
          return m;
        })
      );

      if (activeMeeting && (activeMeeting.id === shareTargetMeeting.id || activeMeeting.meeting_id === shareTargetMeeting.meeting_id)) {
        setActiveMeeting((prev) => {
          if (!prev) return null;
          const currentParts = prev.participants || [];
          if (!currentParts.some((p) => p.email === email)) {
            return { ...prev, participants: [...currentParts, newParticipant] };
          }
          return prev;
        });
      }

      setToastMessage(`Meeting shared with ${email}`);
      setShareEmail('');
      setShareDialogOpen(false);
    } catch (err: any) {
      setShareError(err.message || 'Failed to share meeting.');
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, height: '100%', overflowY: 'auto', bgcolor: 'var(--hp-bg)' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
            Meetings Intelligence
          </Typography>
          <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ mt: 0.5 }}>
            Real-time Cloud Run synchronized recordings, transcripts, and participants
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => loadMeetings(true)}
            startIcon={<Refresh sx={{ animation: isLoading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              borderColor: 'var(--hp-border)',
              color: 'var(--hp-text)',
              fontSize: '0.84rem',
              fontWeight: 600,
              bgcolor: 'var(--hp-surface)'
            }}
          >
            Sync from Cloud Run
          </Button>

          <Button
            variant="contained"
            onClick={() => navigate('/workspace')}
            startIcon={<Dashboard />}
            sx={{
              bgcolor: 'var(--hp-primary)',
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 600,
              fontSize: '0.84rem',
              boxShadow: '0 4px 12px rgba(79, 107, 255, 0.25)',
              '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
            }}
          >
            Open in Workspace
          </Button>
        </Box>
      </Box>

      {/* Tabs & Search Filter Bar */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: '36px',
            bgcolor: 'var(--hp-surface)',
            borderRadius: '12px',
            p: 0.5,
            border: '1px solid var(--hp-border)',
            '& .MuiTab-root': {
              minHeight: '32px',
              textTransform: 'none',
              fontSize: '0.84rem',
              fontWeight: 600,
              color: 'var(--hp-text-secondary)',
              py: 0.5,
              px: 2,
              borderRadius: '8px'
            },
            '& .Mui-selected': {
              color: 'var(--hp-primary)',
              bgcolor: 'var(--hp-surface-raised)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
            },
            '& .MuiTabs-indicator': { display: 'none' }
          }}
        >
          <Tab label="Today" />
          <Tab label="Past" />
          <Tab label="My Meetings" />
        </Tabs>

        <Box className="neumorphic-input" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.6, width: { xs: '100%', sm: 280 } }}>
          <Search sx={{ color: 'var(--hp-text-secondary)', fontSize: 18 }} />
          <InputBase
            placeholder="Search meetings by title or host..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, fontSize: '0.85rem', color: 'var(--hp-text)' }}
          />
          {searchQuery && (
            <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0.2 }}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Meetings Grid List */}
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1.5 }}>
          <CircularProgress size={32} sx={{ color: 'var(--hp-primary)' }} />
          <Typography variant="body2" color="var(--hp-text-secondary)">
            Fetching your meetings from Cloud Run & Firestore...
          </Typography>
        </Box>
      ) : filteredMeetings.length === 0 ? (
        <Box className="neumorphic-card" sx={{ p: 5, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <CalendarToday sx={{ fontSize: 44, color: 'var(--hp-primary)', opacity: 0.6 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
            No Meetings Found
          </Typography>
          <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ maxWidth: 420 }}>
            {tab === 0
              ? 'No meetings found for Today. Use the Notetaker or trigger a webhook to sync a session.'
              : tab === 1
              ? 'No past recordings stored.'
              : 'No meetings found for your account email.'}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => loadMeetings(true)}
            startIcon={<Refresh />}
            sx={{ mt: 1, borderRadius: '10px', textTransform: 'none' }}
          >
            Refresh Meetings
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filteredMeetings.map((meeting) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={meeting.id}>
              <Box className="neumorphic-card" sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, p: 2.2 }}>
                {/* Meeting Card Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1.2, minWidth: 0 }}>
                    <Avatar sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', width: 38, height: 38 }}>
                      <CalendarToday fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.96rem', color: 'var(--hp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {meeting.title}
                      </Typography>
                      <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ display: 'block' }}>
                        {meeting.date} • {meeting.time}
                      </Typography>
                    </Box>
                  </Box>

                  <Tooltip title="Share with teammate">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShareTargetMeeting(meeting);
                        setShareDialogOpen(true);
                      }}
                      sx={{ bgcolor: 'var(--hp-surface)', color: 'var(--hp-primary)', border: '1px solid var(--hp-border)' }}
                    >
                      <PersonAdd fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Status Badges */}
                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                  {meeting.audioUrl && (
                    <Chip
                      icon={<VolumeUp sx={{ fontSize: 13 }} />}
                      label={`Audio (${meeting.duration})`}
                      size="small"
                      sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(34, 201, 154, 0.15)', color: '#168A69', fontWeight: 600 }}
                    />
                  )}
                  {meeting.transcriptUrl && (
                    <Chip
                      icon={<CloudDone sx={{ fontSize: 13 }} />}
                      label="Transcript"
                      size="small"
                      sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(79, 107, 255, 0.15)', color: 'var(--hp-primary)', fontWeight: 600 }}
                    />
                  )}
                  {meeting.host_email && (
                    <Chip
                      label={`Host: ${formatNameFromEmail(meeting.host_email)}`}
                      size="small"
                      sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'var(--hp-surface)' }}
                    />
                  )}
                </Box>

                {/* Participants Avatars */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 1, borderTop: '1px solid var(--hp-border)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.75rem' } }}>
                      {meeting.participants.map((p) => (
                        <Tooltip key={p.id} title={`${p.name} (${p.email || 'Participant'})`}>
                          <Avatar alt={p.name} src={p.avatar}>
                            {p.name.charAt(0)}
                          </Avatar>
                        </Tooltip>
                      ))}
                    </AvatarGroup>
                    <Typography variant="caption" color="var(--hp-text-secondary)">
                      {meeting.participants.length} participant{meeting.participants.length === 1 ? '' : 's'}
                    </Typography>
                  </Box>

                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setActiveMeeting(meeting)}
                    startIcon={<PlayArrow sx={{ fontSize: 16 }} />}
                    sx={{
                      bgcolor: 'var(--hp-primary)',
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.76rem',
                      py: 0.3,
                      px: 1.2,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
                    }}
                  >
                    Play & Transcript
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Meeting Player & Synchronized Transcript Modal Dialog */}
      <Dialog
        open={Boolean(activeMeeting)}
        onClose={() => setActiveMeeting(null)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: '20px',
              bgcolor: 'var(--hp-bg)',
              height: '80vh',
              maxHeight: 700,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column'
            }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
            {activeMeeting?.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const target = activeMeeting;
                setActiveMeeting(null);
                navigate('/workspace');
              }}
              startIcon={<Dashboard sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem' }}
            >
              Open in Workspace
            </Button>
            <IconButton size="small" onClick={() => setActiveMeeting(null)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {activeMeeting && (
            <MeetingDetails
              meeting={activeMeeting}
              autoPlay={true}
              onParticipantAdded={(id, email) => {
                const newName = formatNameFromEmail(email);
                const newPart = {
                  id: `p-${Date.now()}-${email}`,
                  name: newName,
                  email,
                  avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newName)}&background=FF6577&color=fff`
                };
                setMeetings((prev) =>
                  prev.map((m) => (m.id === id ? { ...m, participants: [...m.participants, newPart] } : m))
                );
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => !isSharing && setShareDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: '16px', p: 1, bgcolor: 'var(--hp-surface)' }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Share Meeting
          </Typography>
          <IconButton size="small" onClick={() => setShareDialogOpen(false)} disabled={isSharing}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleShareSubmit}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ fontSize: '0.82rem' }}>
              Share <strong>{shareTargetMeeting?.title}</strong> recording and transcript with your team.
            </Typography>

            <TextField
              autoFocus
              label="Teammate Email"
              type="email"
              placeholder="e.g. colleague@company.com"
              value={shareEmail}
              onChange={(e) => {
                setShareEmail(e.target.value);
                setShareError(null);
              }}
              fullWidth
              size="small"
              required
              disabled={isSharing}
            />

            {shareError && (
              <Alert severity="error" sx={{ borderRadius: '8px', fontSize: '0.78rem' }}>
                {shareError}
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShareDialogOpen(false)} disabled={isSharing} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSharing || !shareEmail.trim()}
              startIcon={isSharing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PersonAdd sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: 'var(--hp-primary)', borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
            >
              {isSharing ? 'Sharing...' : 'Share'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={3000}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastMessage(null)} severity="info" sx={{ borderRadius: '12px' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MeetingsPage;
